import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronRight } from 'lucide-react';

const getDayEventList = (day) => {
  if (!day) return [];
  if (Array.isArray(day.events) && day.events.length > 0) {
    return day.events.map((e, idx) => ({
      time: (typeof e === 'object' ? e.time : null) || (Array.isArray(day.timings) ? day.timings[idx] : '') || '',
      activity: (typeof e === 'object' ? e.title || e.activity : e) || (Array.isArray(day.remarks) ? day.remarks[idx] : '') || '',
      location: (typeof e === 'object' ? e.description || e.location || '' : '')
    })).filter(e => e.activity || e.time);
  }
  if (Array.isArray(day.timings) && day.timings.length > 0) {
    return day.timings.map((t, idx) => ({
      time: typeof t === 'object' ? (t.time || '') : (t || ''),
      activity: typeof t === 'object' ? (t.activity || t.title || '') : (day.remarks?.[idx] || ''),
      location: typeof t === 'object' ? (t.location || '') : ''
    })).filter(e => e.activity || e.time);
  }
  if (Array.isArray(day.remarks) && day.remarks.length > 0) {
    return day.remarks.map((r, idx) => ({
      time: (Array.isArray(day.timings) && day.timings[idx]) || '',
      activity: r || '',
      location: ''
    })).filter(e => e.activity);
  }
  return [];
};

const ItineraryCard = ({ voucher, id }) => {
  const navigate = useNavigate();
  const voucherId = id || voucher?._id || voucher?.booking_id;
  const itinerary = voucher?.itinerary || [];

  const now = new Date();
  let activeItin = itinerary.find(itin => {
    if (!itin.date) return false;
    return new Date(itin.date).toDateString() === now.toDateString();
  });
  if (!activeItin && itinerary.length > 0) activeItin = itinerary[0];

  const activeDayEvents = getDayEventList(activeItin);

  const handleOpenFullItinerary = () => {
    navigate(`/voucher-itinerary/${voucherId}`);
  };

  return (
    <div 
      className="vc-card vc-expandable cursor-pointer group" 
      onClick={handleOpenFullItinerary}
      title="Open full interactive itinerary workspace"
    >
      <div className="vc-card-head flex items-center justify-between pb-2 border-b border-slate-100">
        <h3 className="flex items-center gap-2 text-sm font-medium text-slate-600 tracking-tight">
          <span className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
            <Calendar size={15} className="text-[#067978]" />
          </span>
          <span>Trip Itinerary</span>
        </h3>
      </div>

      {activeItin ? (
        <>
          {(activeItin.day_title || activeItin.title || activeItin.day) && (
            <div
              className="text-[13px] font-medium text-slate-700 truncate mt-2.5 mb-1 group-hover:text-[#067978] transition-colors"
              title={activeItin.day_title || activeItin.title || activeItin.day}
            >
              {activeItin.day_title || activeItin.title || activeItin.day}
            </div>
          )}
          <div className="vc-today-list mt-2 space-y-2">
            {activeDayEvents.length > 0 ? (
              activeDayEvents.slice(0, 3).map((evt, idx) => (
                <div key={idx} className="flex items-start justify-between py-1 group/item">
                  {/* Left side: activity title + description */}
                  <div className="flex items-start gap-2.5 min-w-0 flex-1 pr-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#067978] mt-[7px] shrink-0 opacity-80" />
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-xs sm:text-[13px] font-normal text-slate-600 truncate group-hover/item:text-[#067978] transition-colors"
                        title={evt.activity}
                      >
                        {evt.activity}
                      </div>
                      {evt.location && (
                        <div
                          className="text-[11px] text-slate-400 font-normal mt-0.5 truncate"
                          title={evt.location}
                        >
                          {evt.location}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Right side: time */}
                  {evt.time && (
                    <div className="text-xs text-slate-400 font-normal shrink-0 ml-3">
                      {evt.time}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-xs text-slate-400 py-1">No activities listed for this day.</div>
            )}
          </div>
          <div className="vc-expand-cue mt-auto pt-3 flex items-center gap-1 text-xs font-normal text-[#067978]">
            <span>View full itinerary</span>
            <ChevronRight size={14} className="text-[#067978] group-hover:translate-x-0.5 transition-transform" />
          </div>
        </>
      ) : (
        <>
          <div className="text-sm text-slate-500 py-2 mt-2">
            <div className="font-medium text-slate-700">No trip itinerary attached</div>
            <div className="text-xs text-slate-400 mt-0.5">Add an itinerary to start planning this trip.</div>
          </div>
          <div className="vc-expand-cue mt-auto pt-3 flex items-center gap-1 text-xs font-medium text-[#067978]">
            <span>Add Itinerary</span>
            <ChevronRight size={14} className="text-[#067978] group-hover:translate-x-0.5 transition-transform" />
          </div>
        </>
      )}
    </div>
  );
};

export default ItineraryCard;
